import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  LayoutList, Search, Activity, FileCode, ScrollText, Users, MailWarning,
  LogOut, Rocket, Radio, ClipboardCheck, Contact, KeyRound, Loader2, Landmark, Gauge,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/app", label: "Ticket Queue", icon: LayoutList, testid: "nav-queue", end: true },
  { to: "/health-checks", label: "Health Checks", icon: ClipboardCheck, testid: "nav-healthchecks" },
  { to: "/taric", label: "TARIC Lookup", icon: Search, testid: "nav-taric" },
  { to: "/declarations", label: "ZZI Declarations", icon: FileCode, testid: "nav-declarations" },
  { to: "/fiscal-reps", label: "Fiscal Reps", icon: Landmark, testid: "nav-fiscal-reps", adminOnly: true },
  { to: "/usage", label: "Usage & Budget", icon: Gauge, testid: "nav-usage", adminOnly: true },
  { to: "/threads", label: "Threads", icon: MailWarning, testid: "nav-threads" },
  { to: "/leads", label: "Leads", icon: Contact, testid: "nav-leads", adminOnly: true },
  { to: "/ops", label: "Ops Health", icon: Activity, testid: "nav-ops" },
  { to: "/audit", label: "Audit Log", icon: ScrollText, testid: "nav-audit" },
  { to: "/users", label: "Users", icon: Users, testid: "nav-users", adminOnly: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const { data: health } = useQuery({
    queryKey: ["ops-health-mini"],
    queryFn: async () => (await api.get("/ops/health")).data,
    refetchInterval: 20000,
  });

  const doLogout = () => { logout(); navigate("/login"); };
  const pollerUp = health?.poller_running;

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <aside className="w-56 shrink-0 border-r border-zinc-800 bg-zinc-950 flex flex-col">
        <div className="h-14 flex items-center gap-2 px-4 border-b border-zinc-800">
          <Rocket className="h-5 w-5 text-blue-500" />
          <span className="font-heading font-semibold tracking-tight text-sm">ROKET</span>
          <span className="ml-auto text-[9px] font-mono text-zinc-600 uppercase">Customs Bot</span>
        </div>
        <nav className="flex-1 py-2">
          {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-4 py-2 text-sm border-l-2 transition-colors duration-150",
                isActive ? "border-blue-500 bg-zinc-900 text-white" : "border-transparent text-zinc-400 hover:text-white hover:bg-zinc-900/60")}>
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-zinc-800 p-3 space-y-2">
          <div className="flex items-center gap-2 text-[10px] font-mono">
            <Radio className={cn("h-3 w-3", pollerUp ? "text-emerald-500" : "text-red-500")} />
            <span className={pollerUp ? "text-emerald-400" : "text-red-400"}>
              POLLER {pollerUp ? "ONLINE" : "OFFLINE"}
            </span>
            {health?.zzi_safe_mode && <span className="ml-auto text-amber-500">ZZI SAFE</span>}
          </div>
          <div className="text-[10px] font-mono text-zinc-500 truncate">{user?.email}</div>
          <button data-testid="change-password-btn" onClick={() => setPwOpen(true)}
            className="w-full flex items-center gap-2 text-xs text-zinc-400 hover:text-white transition-colors duration-150">
            <KeyRound className="h-3.5 w-3.5" /> Change password
          </button>
          <button data-testid="logout-btn" onClick={doLogout}
            className="w-full flex items-center gap-2 text-xs text-zinc-400 hover:text-red-400 transition-colors duration-150">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-x-hidden">{children}</main>
      {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed successfully");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to change password");
    } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md bg-zinc-950/80 p-4" onClick={onClose}>
      <form onSubmit={submit} data-testid="change-password-form" onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-zinc-900 border border-zinc-800 rounded-sm p-5">
        <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><KeyRound className="h-4 w-4 text-blue-500" /> Change password</h3>
        <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Current password</label>
        <input data-testid="cp-current" type="password" required value={cur} onChange={(e) => setCur(e.target.value)}
          className="w-full mb-3 bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm font-mono focus:border-blue-500 outline-none" />
        <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">New password</label>
        <input data-testid="cp-new" type="password" required value={next} onChange={(e) => setNext(e.target.value)}
          className="w-full mb-4 bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm font-mono focus:border-blue-500 outline-none" />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm px-3 py-2 border border-zinc-700 rounded-sm hover:bg-zinc-800">Cancel</button>
          <button type="submit" data-testid="cp-submit" disabled={loading}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-sm px-4 py-2 rounded-sm">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Update
          </button>
        </div>
      </form>
    </div>
  );
}
